/* eslint-disable max-lines, react/no-array-index-key, react/jsx-props-no-spreading */
/* eslint max-len: ["error", 150] */
import PropTypes from 'prop-types';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Grid,
  IconButton,
  InputBase,
  InputLabel,
  MenuItem,
  TextField as MuiTextField,
  Paper,
  Popover,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { format, isAfter, startOfDay, subDays } from 'date-fns';
import { useEffect, useRef, useState } from 'react';
import { Field, Form, useFormState } from 'react-final-form';
import { useDispatch, useSelector } from 'react-redux';
import { inventoryLotsByLotIdApi } from 'api/inventory';
import DateField from 'common/forms/DateField';
import MarkdownField from 'common/forms/MarkdownField';
import SelectField from 'common/forms/SelectField';
import TextField from 'common/forms/TextField';
import { validateRequired } from 'common/forms/formValidations';
import { createInventoryReceiving } from 'store/thunks/stockManagementThunk';
import { selectAccessibleFacilities } from 'store/selectors/shipmentModuleSelectors';
import { selectActiveFulfillingPharmacies } from 'store/selectors/facilitySelectors';
import { getStateDisplayName } from 'store/utils/shipmentUtils';
import EXISTING_LOT_REASON_OPTIONS from 'common/constants/existingLotReasons';
import TALLY_REASON_OPTIONS from 'common/constants/tallyReasons';
import SYSTEM_DEFICIT from 'common/constants/inventoryLabels';
import {
  NO_PASTE_INPUT_PROPS,
  areLineQuantitiesValid,
  budFieldError,
  describeLotDrug,
  formatPreviewQty,
  formatUnverifiedQty,
  getBudHelperText,
  getBudMinDate,
  getExistingLotForNumber,
  getLotConfirmHelperText,
  getLotHelperText,
  getLotNdcMismatch,
  getNdcHelperText,
  getOffage,
  getOrderedHelperText,
  getReceivedHelperText,
  getSendableOffageReasons,
  getShippedHelperText,
  getTallyState,
  getUnverifiedAtPharmacy,
  isBudValid,
  isExistingLotReasonComplete,
  isLotConfirmed,
  isOffageReasonsValid,
  isTallyComplete,
  isValidLotNumber,
  isValidNdc,
  lotConfirmFieldError,
  lotFieldError,
  needsExistingLotReason,
  orderedFieldError,
  receivedFieldError,
  resolveBudFromLot,
  seedOffageReasonRow,
  shippedFieldError,
} from '../utils/stockLineValidation';
import { useVendorContext } from '../context/VendorContext';
import { useStockReceivingDraft } from '../context/StockReceivingDraftContext';
import AddCatalogDialog from './AddCatalogDialog';
import AddInventoryItemDialog from './AddInventoryItemDialog';
import NdcAutocomplete from './NdcAutocomplete';
import OffageReasonsSection from './OffageReasonsSection';

const EMPTY_SPLIT = { fulfillingPharmacyId: '', quantityExpected: '' };

// Streams the live vendor/receipt form values into the shared draft so a close + reopen (or a
// refresh) can restore them. useFormState + effect defers the write to after render, so the draft
// update never runs during the form's render phase.
const PersistDraftVendorForm = ({ onChange }) => {
  const { values } = useFormState({ subscription: { values: true } });
  useEffect(() => {
    onChange(values);
  }, [values, onChange]);
  return null;
};

PersistDraftVendorForm.propTypes = {
  onChange: PropTypes.func.isRequired,
};

// The BUD field's full name is long enough to overlap the calendar icon while the label sits inside
// the box. Show a short "BUD *" at rest (no overlap, and the "BUD" size never changes) and reveal
// the full "Beyond-Use Date *" only on focus, once the label has floated clear of the icon.
const BudDateField = ({ value, onChange, error, helperText, disabled }) => {
  const [focused, setFocused] = useState(false);
  return (
    <DesktopDatePicker
      // Remounted when the lock flips, which is the only way the field's own text catches up. The
      // masked input keeps the string the operator typed in state of its own and rifm re-syncs it
      // a render late, so adopting the existing lot's date (POM-6111) left the OLD date on
      // display in a field that had just locked - reading as a receipt for a date nobody chose.
      key={disabled ? 'bud-locked' : 'bud-open'}
      label={focused ? 'Beyond-Use Date *' : 'BUD *'}
      inputFormat='MM/dd/yyyy'
      disabled={disabled}
      // Stock is sometimes received against a lot that has already passed its BUD, so backdated
      // values are allowed within the lookback window. Inclusive floor, matching isBudValid.
      minDate={getBudMinDate()}
      value={value}
      onChange={onChange}
      renderInput={(params) => (
        <MuiTextField
          {...params}
          size='small'
          fullWidth
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          InputLabelProps={{ sx: { fontSize: '0.78rem' } }}
          error={error}
          helperText={helperText}
        />
      )}
    />
  );
};

BudDateField.propTypes = {
  value: PropTypes.instanceOf(Date),
  onChange: PropTypes.func.isRequired,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  disabled: PropTypes.bool,
};

BudDateField.defaultProps = {
  value: null,
  error: false,
  helperText: '',
  disabled: false,
};

const REQUIRED_LINE_FIELDS = [
  'ndc',
  'lotNumber',
  'quantityOrdered',
  'quantityShipped',
  'totalQuantityReceived',
];

const isSplitValid = (split) =>
  split.fulfillingPharmacyId.trim() !== '' && Number(split.quantityExpected) >= 1;

const isDirtySplit = (split) =>
  split.fulfillingPharmacyId !== '' || String(split.quantityExpected).trim() !== '';

const isLineValid = (line) => {
  const baseValid = REQUIRED_LINE_FIELDS.every((f) => String(line[f]).trim() !== '');
  if (!baseValid) return false;
  if (!isValidNdc(line.ndc)) return false;
  if (!isValidLotNumber(line.lotNumber)) return false;
  if (!isLotConfirmed(line.lotNumber, line.confirmLotNumber)) return false;
  if (!isBudValid(line.bud)) return false;
  if (!areLineQuantitiesValid(line)) return false;
  const dirtySplits = (line.splits ?? []).filter(isDirtySplit);
  if (dirtySplits.length === 0) return true;
  const received = Number(line.totalQuantityReceived) || 0;
  return (
    dirtySplits.every(isSplitValid) &&
    dirtySplits.reduce((sum, s) => sum + (Number(s.quantityExpected) || 0), 0) <= received
  );
};

const getSplitChipColor = (overflow, remaining) => {
  if (overflow) return 'error';
  if (remaining === 0) return 'success';
  return 'default';
};

const getLineStatus = (line, ndcSel) => {
  if (!isLineValid(line) || !ndcSel || !isOffageReasonsValid(line)) return 'incomplete';
  return 'complete';
};

const NOTICE_TONES = {
  warning: {
    bgcolor: '#fff8e1',
    accent: (theme) => theme.palette.warning.warningColour ?? theme.palette.warning.main,
  },
  error: {
    bgcolor: '#fdecea',
    accent: (theme) => theme.palette.error.main,
  },
};

const LineNotice = ({ tone, title, detail, testId }) => {
  const palette = NOTICE_TONES[tone] ?? NOTICE_TONES.warning;
  return (
    <Box
      role='alert'
      data-testid={testId}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 1.5,
        py: 0.75,
        mt: -1.25,
        mb: 2.5,
        borderRadius: '4px',
        bgcolor: palette.bgcolor,
        borderLeft: '4px solid',
        borderLeftColor: palette.accent,
      }}
    >
      <Typography variant='caption' component='p' sx={{ lineHeight: 1.5, m: 0 }}>
        <WarningAmberIcon
          sx={{ fontSize: 15, color: palette.accent, verticalAlign: 'text-bottom', mr: 0.75 }}
        />
        <Box component='span' sx={{ fontWeight: 600 }}>
          {title}.
        </Box>{' '}
        <Box component='span' sx={{ color: 'text.secondary' }}>
          {detail}
        </Box>
      </Typography>
    </Box>
  );
};

LineNotice.propTypes = {
  tone: PropTypes.oneOf(['warning', 'error']).isRequired,
  title: PropTypes.string.isRequired,
  detail: PropTypes.string.isRequired,
  testId: PropTypes.string,
};

LineNotice.defaultProps = {
  testId: undefined,
};

const AddStockDialog = ({ open, onClose }) => {
  const dispatch = useDispatch();
  // Draft state lives in the module-scoped context (backed by sessionStorage) so line items, the
  // per-line lookups, and the vendor form all survive a close + reopen and a page refresh.
  const {
    lines,
    setLines,
    ndcDrugName,
    setNdcDrugName,
    ndcSelected,
    setNdcSelected,
    lineExpanded,
    setLineExpanded,
    vendorIsFromList,
    setVendorIsFromList,
    vendorForm,
    setVendorForm,
    createLine,
    resetDraft,
    hasDraft,
  } = useStockReceivingDraft();
  const [submitting, setSubmitting] = useState(false);
  const [showLineErrors, setShowLineErrors] = useState(false);
  const [lotLookups, setLotLookups] = useState({});
  const [distributionOpen, setDistributionOpen] = useState({});
  const lotLookupSeq = useRef({});
  const [howItWorksAnchor, setHowItWorksAnchor] = useState(null);
  const [catalogDialogOpen, setCatalogDialogOpen] = useState(false);
  const [ndcDialogOpen, setNdcDialogOpen] = useState(false);
  // Shown when the dialog reopens onto an existing draft — the user chooses resume vs start new.
  const [resumePromptOpen, setResumePromptOpen] = useState(false);
  // Captured on open (and on start-new) so streaming the typed values into the draft never
  // reinitialises the form mid-edit; only these two moments change what the form seeds from.
  const [formInitialValues, setFormInitialValues] = useState(vendorForm);
  // Bumped whenever the form must reset to a new snapshot (open, start-new, submit). Changing the
  // key remounts the whole form subtree, so controlled children like the freeSolo vendor
  // Autocomplete can't keep stale display text — an in-place RFF reinitialise leaves the visible
  // input value behind even after the field value is cleared.
  const [formResetKey, setFormResetKey] = useState(0);
  const prevOpenRef = useRef(false);
  const {
    vendors,
    loading: vendorLoading,
    loadingMore,
    hasMore,
    loadInitial,
    loadMore,
  } = useVendorContext();

  const itemsPanelRef = useRef(null);

  const isLineExpanded = (id) => lineExpanded[id] !== false;
  const handleToggleLine = (id) =>
    setLineExpanded((prev) => ({ ...prev, [id]: !isLineExpanded(id) }));

  // POM-5827: active-only allowed pharmacies (auth list can include inactive ones).
  const fulfillingPharmacies = useSelector(selectActiveFulfillingPharmacies);

  // auth.fulfillingPharmacies carries only id + name; the pharmacy store (loaded app-wide in
  // App.js) is what has `state`. Same "Name (State)" shape the other facility pickers use, and it
  // falls back to the bare name if the state isn't there.
  const accessibleFacilities = useSelector(selectAccessibleFacilities);
  const facilityLabel = (fulfillingPharmacyId, fulfillingPharmacyName) => {
    const facilityState = accessibleFacilities.find(
      (f) => f?.fulfillingPharmacyId === fulfillingPharmacyId
    )?.state;
    return facilityState
      ? `${fulfillingPharmacyName} (${getStateDisplayName(facilityState)})`
      : fulfillingPharmacyName;
  };

  const pharmacyNameById = (fulfillingPharmacyId) =>
    fulfillingPharmacies.find((p) => p.fulfillingPharmacyId === fulfillingPharmacyId)
      ?.fulfillingPharmacyName ?? fulfillingPharmacyId;

  const clearLotLookup = (lineId) => {
    lotLookupSeq.current[lineId] = (lotLookupSeq.current[lineId] ?? 0) + 1;
    setLotLookups((prev) => {
      const { [lineId]: removed, ...rest } = prev;
      return rest;
    });
    // The lot number no longer resolves, so the "replaced from" notice is about a lot that is no
    // longer in play. The BUD itself is left alone — it is the user's field again now that
    // nothing locks it.
    setLines((prev) =>
      prev.map((l) => (l.id === lineId && l.budReplacedFrom ? { ...l, budReplacedFrom: null } : l))
    );
  };

  /**
   * The typed lot number already belongs to a DIFFERENT drug.
   *
   * A manufacturer lot number identifies one product, so this means the operator read the lot
   * off the vial in their hand and recorded it against the wrong line. The row it creates looks
   * entirely legitimate afterwards: it dispenses through FEFO, it accepts claims, and nothing
   * downstream questions it.
   *
   * Gating on it, not merely showing it. The LineNotice alone let the operator submit straight
   * through a red banner, and `upsertInventoryReceiving` has no server-side cross-NDC check to
   * catch it behind them — so this predicate is the only thing standing in the way. The dispense
   * side already refuses the same condition by disabling Add lot.
   *
   * Only once an NDC is chosen: `line.ndc` is what the lot is compared against, and before then
   * there is nothing to disagree with.
   */
  const hasForeignNdcLot = (line) =>
    !!ndcSelected[line.id] &&
    getLotNdcMismatch({ entries: lotLookups[line.id]?.entries ?? [], ndc: line.ndc }).length > 0;

  /**
   * POM-6086 - this line's lot number is already in inventory and has not said why.
   *
   * Reads the same lookup the notice does, so the banner and the gate cannot disagree about
   * whether a line is recordable. Falls silent when a tally is already collecting a reason for
   * the same lot in the same act; a tally of ZERO carries none, so the reason is still owed there.
   */
  const isLineExistingLotReasonComplete = (line, receivedAtPharmacyId) => {
    const entries = lotLookups[line.id]?.entries ?? [];
    const unverified = getUnverifiedAtPharmacy({
      entries,
      fulfillingPharmacyId: receivedAtPharmacyId,
    });

    return isExistingLotReasonComplete({
      existingLot: ndcSelected[line.id]
        ? getExistingLotForNumber({ entries, ndc: line.ndc })
        : null,
      tallyState: unverified
        ? getTallyState({
            unverifiedQty: unverified.unverifiedQty,
            quantityReceived: line.totalQuantityReceived,
            tallyQty: line.tallyQty,
          })
        : null,
      existingLotReason: line.existingLotReason,
    });
  };

  /**
   * The existing-lot reason to SEND, which is only one that is still owed.
   *
   * A reason answered before the operator retyped the lot number describes a lot this line no
   * longer names. Recomputed from the lookup rather than cleared on edit, exactly as the tally
   * is: the payload is built from what the lookup currently says, so a stale entry-only value
   * has no route to the API.
   */
  const getSendableExistingLotReason = (line, receivedAtPharmacyId) => {
    const entries = lotLookups[line.id]?.entries ?? [];
    const unverified = getUnverifiedAtPharmacy({
      entries,
      fulfillingPharmacyId: receivedAtPharmacyId,
    });
    const owed = needsExistingLotReason({
      existingLot: ndcSelected[line.id]
        ? getExistingLotForNumber({ entries, ndc: line.ndc })
        : null,
      tallyState: unverified
        ? getTallyState({
            unverifiedQty: unverified.unverifiedQty,
            quantityReceived: line.totalQuantityReceived,
            tallyQty: line.tallyQty,
          })
        : null,
    });

    return owed ? String(line.existingLotReason ?? '').trim() : '';
  };

  const isLineTallyComplete = (line, receivedAtPharmacyId) => {
    const unverified = getUnverifiedAtPharmacy({
      entries: lotLookups[line.id]?.entries ?? [],
      fulfillingPharmacyId: receivedAtPharmacyId,
    });
    if (!unverified) return true;
    const complete = isTallyComplete({
      unverifiedQty: unverified.unverifiedQty,
      quantityReceived: line.totalQuantityReceived,
      tallyQty: line.tallyQty,
      tallyReason: line.tallyReason,
    });
    if (!complete) return false;
    const state = getTallyState({
      unverifiedQty: unverified.unverifiedQty,
      quantityReceived: line.totalQuantityReceived,
      tallyQty: line.tallyQty,
    });
    const splitTotal = (line.splits ?? [])
      .filter(isDirtySplit)
      .reduce((sum, s) => sum + (Number(s.quantityExpected) || 0), 0);
    const distributable = Math.max(
      0,
      (Number(line.totalQuantityReceived) || 0) - (state.effective ?? 0)
    );
    return splitTotal <= distributable;
  };

  const prefillBudFromLot = ({ lineId, entries, receivedAtPharmacyId }) => {
    setLines((prev) =>
      prev.map((l) => {
        if (l.id !== lineId) return l;
        // The existing lot's BUD OVERWRITES whatever was typed — see resolveBudFromLot for why
        // that precedence is the right way round. Deferring to the typed value left a stale date
        // sitting in a field that then LOCKS, labelled "From the existing lot", showing a date the
        // existing lot never had and giving the user no way to correct it.
        const resolved = resolveBudFromLot({ entries, receivedAtPharmacyId, typedBud: l.bud });
        if (!resolved) return l;
        return { ...l, bud: resolved.bud, budReplacedFrom: resolved.replacedFrom };
      })
    );
  };

  const lookupLotWhenConfirmed = ({
    lineId,
    lotNumber,
    confirmLotNumber,
    receivedAtPharmacyId,
  }) => {
    const trimmed = String(lotNumber ?? '').trim();
    if (!isValidLotNumber(trimmed) || !isLotConfirmed(lotNumber, confirmLotNumber)) {
      clearLotLookup(lineId);
      return;
    }
    if (lotLookups[lineId]?.lotNumber === trimmed) return;
    const seq = (lotLookupSeq.current[lineId] ?? 0) + 1;
    lotLookupSeq.current[lineId] = seq;

    inventoryLotsByLotIdApi({ lotId: trimmed })
      .then((entries) => {
        if (lotLookupSeq.current[lineId] !== seq) return;
        setLotLookups((prev) => ({
          ...prev,
          [lineId]: { lotNumber: trimmed, entries: entries ?? [], failed: false },
        }));
        prefillBudFromLot({ lineId, entries: entries ?? [], receivedAtPharmacyId });
      })
      .catch(() => {
        if (lotLookupSeq.current[lineId] !== seq) return;
        setLotLookups((prev) => ({
          ...prev,
          [lineId]: { lotNumber: trimmed, entries: [], failed: true },
        }));
      });
  };

  /**
   * Re-run the lot lookups a resumed draft cannot bring back with it.
   *
   * The draft persists what was TYPED; `lotLookups` is component state and the dialog unmounts
   * on close, so a resumed row returns without the one thing every lot-aware control reads. That
   * is not cosmetic: the existing-lot warning and its reason picker, the BUD lock, the
   * foreign-NDC block and the unverified tally prompt all vanish from a row that still needs
   * them, and the submit gates — reading the same empty lookup — pass a receipt core then
   * refuses, leaving nothing on screen able to satisfy the refusal.
   *
   * Re-fetched rather than persisted into the draft: on-hand, unverified quantity and the set of
   * facilities holding the lot all move while a draft sits, and a stale entry would drive the BUD
   * lock and the tally state off numbers that are no longer true. Idempotent — the fetch is
   * skipped for a line whose lookup already matches, and re-applying the BUD is a no-op because
   * the value in the field is by then the lot’s own date.
   */
  const restoreLotLookups = (receivedAtPharmacyId) => {
    lines.forEach((line) =>
      lookupLotWhenConfirmed({
        lineId: line.id,
        lotNumber: line.lotNumber,
        confirmLotNumber: line.confirmLotNumber,
        receivedAtPharmacyId,
      })
    );
  };

  const receivedDateLookbackDays = Number(process.env.REACT_APP_RECEIVED_DATE_LOOKBACK_DAYS) || 14;
  // Earliest allowed day, normalized to start-of-day so the boundary doesn't drift with time-of-day.
  // validateMinDate uses strict isAfter, so a minDate of (today - 14) yields exactly 14 valid days
  // including today (today-13 .. today). shouldDisableDate below disables the same boundary day in
  // the picker, which otherwise treats minDate as inclusive and leaves one extra day clickable.
  const receivedDateMinDate = startOfDay(subDays(new Date(), receivedDateLookbackDays));

  // New lines go on top so the row being worked on is always the first one. The panel is scrolled
  // back up too, otherwise a prepended card lands above the viewport and the click looks like a no-op.
  // Finished lines collapse on the way, keeping the list short; incomplete ones stay open so it
  // remains obvious which of them still needs attention.
  const handleAddLine = () => {
    setLineExpanded((prev) => {
      const next = { ...prev };
      lines.forEach((line) => {
        if (getLineStatus(line, ndcSelected[line.id]) === 'complete') next[line.id] = false;
      });
      return next;
    });
    setLines((prev) => [createLine(), ...prev]);
    itemsPanelRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // The id-keyed lookups are pruned along with the line. Stale ids can no longer collide with live
  // ones, but the dialog stays mounted while closed, so without this they grow for the page's life.
  const handleRemoveLine = (id) => {
    const dropId = (prev) => {
      const { [id]: removed, ...rest } = prev;
      return rest;
    };
    setLines((prev) => prev.filter((line) => line.id !== id));
    setNdcDrugName(dropId);
    setNdcSelected(dropId);
    setLineExpanded(dropId);
    setLotLookups(dropId);
    setDistributionOpen(dropId);
  };
  const handleLineChange = (idx, field, value) =>
    setLines((prev) =>
      prev.map((line, i) => {
        if (i !== idx) return line;
        const updated = { ...line, [field]: value };
        return field === 'quantityShipped' || field === 'totalQuantityReceived'
          ? seedOffageReasonRow(updated)
          : updated;
      })
    );

  const handleAddSplit = (lineIdx) =>
    setLines((prev) =>
      prev.map((line, i) =>
        i === lineIdx ? { ...line, splits: [...line.splits, { ...EMPTY_SPLIT }] } : line
      )
    );

  const handleRemoveSplit = (lineIdx, splitIdx) =>
    setLines((prev) =>
      prev.map((line, i) =>
        i === lineIdx ? { ...line, splits: line.splits.filter((_, si) => si !== splitIdx) } : line
      )
    );

  const handleSplitChange = (lineIdx, splitIdx, field, value) =>
    setLines((prev) =>
      prev.map((line, i) =>
        i === lineIdx
          ? {
              ...line,
              splits: line.splits.map((s, si) => (si === splitIdx ? { ...s, [field]: value } : s)),
            }
          : line
      )
    );

  // Closing keeps the draft intact — no confirmation, since nothing is lost. Only transient
  // validation styling is cleared so a reopened draft doesn't start covered in errors.
  const handleClose = () => {
    setShowLineErrors(false);
    onClose();
  };

  // Reopening onto a saved draft asks whether to resume or start fresh. Resuming restores the
  // lot lookups alongside the typed values; start-new wipes both and reseeds an empty form.
  const handleResumeDraft = () => {
    restoreLotLookups(vendorForm?.receivedAtPharmacyId);
    setResumePromptOpen(false);
  };

  const handleStartNew = () => {
    // Line ids restart at 1 on a reset, so a lookup left behind under an old id would attach
    // itself to a brand-new row. Every sequence is bumped too, so a response still in flight
    // from the abandoned draft is discarded instead of re-adding itself afterwards.
    lines.forEach((line) => {
      lotLookupSeq.current[line.id] = (lotLookupSeq.current[line.id] ?? 0) + 1;
    });
    setLotLookups({});
    resetDraft();
    setFormInitialValues({});
    setFormResetKey((k) => k + 1);
    setShowLineErrors(false);
    setResumePromptOpen(false);
  };

  // On the open transition, seed the form from the draft and surface the resume prompt only when
  // there is real unsubmitted content. Runs on the rising edge only, so typing can't re-trigger it.
  // The key bump remounts the form so it cleanly picks up the freshly-seeded initial values.
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      setFormInitialValues(hasDraft ? vendorForm : {});
      setFormResetKey((k) => k + 1);
      setResumePromptOpen(hasDraft);
    }
    prevOpenRef.current = open;
  }, [open, hasDraft, vendorForm]);

  const handleSubmit = (formData) => {
    setSubmitting(true);

    const input = {
      vendorName: formData.vendorName,
      manifestReference: formData.manifestReference,
      receivedDate: formData.receivedDate
        ? format(new Date(formData.receivedDate), 'yyyy-MM-dd')
        : undefined,
      receivedAtPharmacyId: formData.receivedAtPharmacyId,
      documentUrl: formData.documentUrl || undefined,
      notes: formData.notes || undefined,
      lines: lines.map((l) => ({
        ndc: l.ndc,
        lotNumber: l.lotNumber,
        bud: format(l.bud, 'yyyy-MM-dd'),
        quantityOrdered: Number(l.quantityOrdered),
        quantityShipped: Number(l.quantityShipped),
        totalQuantityReceived: Number(l.totalQuantityReceived),
        ...(() => {
          const validSplits = l.splits.filter(isDirtySplit);
          return validSplits.length > 0
            ? {
                splits: validSplits.map((s) => ({
                  fulfillingPharmacyId: s.fulfillingPharmacyId,
                  quantityExpected: Number(s.quantityExpected),
                })),
              }
            : {};
        })(),
        // POM-6086 - a GraphQL ENUM, so an empty string is a request-level validation error
        // rather than a null: it has to be omitted, not blanked. Absent whenever the lot number
        // is new, or when the tally below is carrying the reason instead.
        ...(() => {
          const reason = getSendableExistingLotReason(l, formData.receivedAtPharmacyId);
          return reason ? { existingLotReason: reason } : {};
        })(),
        ...(() => {
          const offageReasons = getSendableOffageReasons(l);
          return offageReasons.length > 0 ? { offageReasons } : {};
        })(),
        ...(() => {
          const unverified = getUnverifiedAtPharmacy({
            entries: lotLookups[l.id]?.entries ?? [],
            fulfillingPharmacyId: formData.receivedAtPharmacyId,
          });
          if (!unverified) return {};
          const state = getTallyState({
            unverifiedQty: unverified.unverifiedQty,
            quantityReceived: l.totalQuantityReceived,
            tallyQty: l.tallyQty,
          });
          return {
            unverifiedTally: {
              inventoryLotId: unverified.inventoryLotId,
              quantity: state.effective ?? 0,
              // `reason` is a GraphQL ENUM, so an empty string is a request-level validation
              // error rather than a null — it has to be omitted, not blanked. It is only ever
              // absent on a zero tally, which the API accepts without one.
              ...(String(l.tallyReason ?? '').trim() ? { reason: l.tallyReason.trim() } : {}),
              ...(String(l.tallyNotes ?? '').trim() ? { notes: l.tallyNotes.trim() } : {}),
            },
          };
        })(),
      })),
    };

    dispatch(
      createInventoryReceiving({
        input,
        onSuccess: () => {
          setSubmitting(false);
          resetDraft();
          setFormInitialValues({});
          setFormResetKey((k) => k + 1);
          setShowLineErrors(false);
          onClose();
        },
      })
    ).finally(() => setSubmitting(false));
  };

  return (
    <>
      <Dialog
        open={open}
        // A stock receipt has a lot of typed input, so an accidental backdrop click or Escape must
        // not dismiss it — closing is only via the explicit Close button. The draft is preserved
        // either way, but this avoids the jarring "where did my form go" moment.
        onClose={(event, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          handleClose();
        }}
        disableEscapeKeyDown
        maxWidth='lg'
        fullWidth
        PaperProps={{ sx: { minHeight: '80vh', display: 'flex', flexDirection: 'column' } }}
      >
        <DialogTitle
          sx={{
            borderBottom: '1px solid',
            borderColor: 'divider',
            pb: 1.5,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Box>
            <Typography variant='h6' sx={{ fontWeight: 600, lineHeight: 1.3 }}>
              New Stock Receipt
            </Typography>
            <Typography variant='body2' color='text.secondary' sx={{ mt: 0.25 }}>
              Record a new stock delivery from a vendor. Fill in the receiving details and add all
              items included in the shipment.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
            <Button
              size='small'
              variant='contained'
              startIcon={<AddIcon />}
              onClick={() => setCatalogDialogOpen(true)}
            >
              Add to Catalog
            </Button>
            <Button
              size='small'
              variant='contained'
              startIcon={<AddIcon />}
              onClick={() => setNdcDialogOpen(true)}
            >
              Add NDC
            </Button>
          </Box>
        </DialogTitle>

        <Form
          key={formResetKey}
          onSubmit={handleSubmit}
          initialValues={formInitialValues}
          render={({ handleSubmit: submitForm, invalid, values }) => (
            <form
              onSubmit={submitForm}
              noValidate
              style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}
            >
              <PersistDraftVendorForm onChange={setVendorForm} />
              <DialogContent sx={{ p: 0, display: 'flex', flex: 1, overflow: 'hidden' }}>
                {/* ── LEFT PANEL ── */}
                <Box
                  sx={{
                    width: 380,
                    flexShrink: 0,
                    bgcolor: 'grey.50',
                    borderRight: '1px solid',
                    borderColor: 'divider',
                    p: 2.5,
                    overflowY: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5,
                  }}
                >
                  <Typography variant='h6' sx={{ fontWeight: 600, mb: 1 }}>
                    Vendor & Receipt Info
                  </Typography>

                  {/* Vendor */}
                  <Box sx={{ mb: 1 }}>
                    <Field
                      name='vendorName'
                      validate={(v) => (!v ? 'Vendor name is required' : undefined)}
                      render={({ input, meta }) => (
                        <FormControl
                          error={!!meta.error && !!meta.touched}
                          fullWidth
                          variant='standard'
                        >
                          <InputLabel shrink required sx={{ fontSize: '1.2em' }}>
                            Vendor Name
                          </InputLabel>
                          <Autocomplete
                            fullWidth
                            freeSolo
                            loading={vendorLoading}
                            options={vendors}
                            getOptionLabel={(opt) => (typeof opt === 'string' ? opt : opt.vendor)}
                            filterOptions={(opts, { inputValue }) => {
                              const q = inputValue.trim().toLowerCase();
                              return q
                                ? opts.filter((o) => o.vendor.toLowerCase().includes(q))
                                : opts;
                            }}
                            value={input.value || null}
                            onOpen={loadInitial}
                            onChange={(_, newVal) => {
                              const name =
                                typeof newVal === 'string' ? newVal : (newVal?.vendor ?? '');
                              input.onChange(name);
                              setVendorIsFromList(typeof newVal === 'object' && newVal !== null);
                            }}
                            onInputChange={(_, inputVal, reason) => {
                              if (reason === 'input') {
                                input.onChange(inputVal);
                                setVendorIsFromList(false);
                              }
                              if (reason === 'clear') {
                                input.onChange('');
                                setVendorIsFromList(false);
                              }
                            }}
                            onBlur={input.onBlur}
                            PaperComponent={({ children: paperChildren, ...paperProps }) => (
                              <Paper {...paperProps}>
                                {paperChildren}
                                {hasMore && (
                                  <Box
                                    onMouseDown={(e) => e.preventDefault()}
                                    sx={{
                                      px: 2,
                                      py: 1,
                                      borderTop: '1px solid',
                                      borderColor: 'divider',
                                    }}
                                  >
                                    <Button
                                      size='small'
                                      fullWidth
                                      onClick={loadMore}
                                      disabled={loadingMore}
                                    >
                                      {loadingMore ? (
                                        <CircularProgress size={14} sx={{ mr: 1 }} />
                                      ) : null}
                                      Load More
                                    </Button>
                                  </Box>
                                )}
                              </Paper>
                            )}
                            sx={{
                              'label + &': { marginTop: '1.3em' },
                              marginTop: '1.3em',
                            }}
                            renderInput={(params) => (
                              <InputBase
                                ref={params.InputProps.ref}
                                inputProps={params.inputProps}
                                endAdornment={
                                  <>
                                    {vendorLoading && (
                                      <CircularProgress color='inherit' size={14} />
                                    )}
                                    {params.InputProps.endAdornment}
                                  </>
                                }
                                fullWidth
                                sx={{
                                  boxSizing: 'border-box',
                                  borderRadius: 1,
                                  marginBottom: 0.25,
                                  padding: '4px 8px',
                                  '&.Mui-focused': { borderWidth: 2, padding: '3px 7px' },
                                }}
                              />
                            )}
                          />
                          {(() => {
                            const vendorHelperText =
                              (meta.touched && meta.error) || (input.value && !vendorIsFromList);
                            const vendorHelperMsg =
                              meta.touched && meta.error
                                ? meta.error
                                : 'Please select a vendor from the list';
                            return (
                              <FormHelperText
                                error={!!(input.value && !vendorIsFromList)}
                                sx={{ minHeight: '0.5rem' }}
                              >
                                {vendorHelperText ? vendorHelperMsg : ''}
                              </FormHelperText>
                            );
                          })()}
                        </FormControl>
                      )}
                    />
                  </Box>

                  {/* Manifest Reference */}
                  <Box sx={{ mb: 1 }}>
                    <TextField
                      label='Manifest Reference'
                      name='manifestReference'
                      id='add-stock-manifestReference'
                      required
                      validations={[validateRequired('Manifest reference is required')]}
                    />
                  </Box>

                  {/* Received Date */}
                  <Box sx={{ mb: 1 }}>
                    <DateField
                      label='Received Date'
                      name='receivedDate'
                      id='add-stock-receivedDate'
                      required
                      minDate={receivedDateMinDate}
                      maxDate={new Date()}
                      shouldDisableDate={(date) => !isAfter(startOfDay(date), receivedDateMinDate)}
                      validations={[validateRequired('Received date is required')]}
                    />
                  </Box>

                  {/* Facility */}
                  <Box sx={{ mb: 1 }}>
                    <SelectField
                      label='Received at Pharmacy'
                      name='receivedAtPharmacyId'
                      id='add-stock-pharmacy'
                      required
                      validations={[validateRequired('Facility is required')]}
                    >
                      {fulfillingPharmacies.map(
                        ({ fulfillingPharmacyId, fulfillingPharmacyName }) => (
                          <MenuItem key={fulfillingPharmacyId} value={fulfillingPharmacyId}>
                            {facilityLabel(fulfillingPharmacyId, fulfillingPharmacyName)}
                          </MenuItem>
                        )
                      )}
                    </SelectField>
                  </Box>

                  {/* Notes */}
                  <Box sx={{ mb: 1.5 }}>
                    <Typography
                      variant='caption'
                      color='text.secondary'
                      sx={{ fontSize: '0.7rem', display: 'block', mb: 0.5 }}
                    >
                      Notes (optional)
                    </Typography>
                    <MarkdownField label='' name='notes' id='add-stock-notes' height='200px' />
                  </Box>
                </Box>

                {/* ── RIGHT PANEL ── */}
                {/* pt is 0 and lives on the sticky Items bar instead: a sticky box can't be shifted
                    outside its containing block (this Box's content box), so any padding-top here
                    would reserve a strip above the stuck bar that cards scroll through. */}
                <Box
                  ref={itemsPanelRef}
                  sx={{ flexGrow: 1, overflowY: 'auto', px: 2.5, pt: 0, pb: 2.5 }}
                >
                  {/* LINE ITEMS header */}
                  {(() => {
                    const doneCount = lines.filter(
                      (l) =>
                        isLineValid(l) &&
                        !!ndcSelected[l.id] &&
                        isOffageReasonsValid(l) &&
                        isLineTallyComplete(l, values.receivedAtPharmacyId) &&
                        isLineExistingLotReasonComplete(l, values.receivedAtPharmacyId) &&
                        !hasForeignNdcLot(l)
                    ).length;
                    const offageCount = lines.filter(
                      (l) => isLineValid(l) && !!ndcSelected[l.id] && getOffage(l) > 0
                    ).length;
                    const todoCount = lines.length - doneCount;
                    return (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          // Pinned so Add Item and the counter stay reachable once the list scrolls.
                          // pt carries the panel's top padding (see the panel Box above) so the bar
                          // itself covers that strip; it must be opaque all the way to the top edge.
                          // common.white rather than background.paper: this theme overrides
                          // palette.background without a `paper` key, so that token resolves to
                          // nothing and the bar renders transparent.
                          position: 'sticky',
                          top: 0,
                          zIndex: 2,
                          bgcolor: 'common.white',
                          pt: 2.5,
                          pb: 2.5,
                        }}
                      >
                        <Typography variant='h6' sx={{ fontWeight: 600 }}>
                          Items
                        </Typography>
                        {lines.length > 0 && (
                          <Typography variant='caption' color='text.secondary'>
                            {lines.length} item{lines.length !== 1 ? 's' : ''}
                            {doneCount > 0 ? ` · ${doneCount} complete` : ''}
                            {offageCount > 0 ? ` · ${offageCount} offage` : ''}
                            {todoCount > 0 ? ` · ${todoCount} incomplete` : ''}
                          </Typography>
                        )}
                        <Box sx={{ flexGrow: 1 }} />
                        <Button
                          size='small'
                          variant='outlined'
                          startIcon={<AddIcon />}
                          onClick={handleAddLine}
                          sx={{ flexShrink: 0 }}
                        >
                          Add Item
                        </Button>
                      </Box>
                    );
                  })()}

                  {lines.length === 0 && (
                    <Typography variant='body2' color='text.secondary' sx={{ py: 1 }}>
                      No items added. Click &quot;Add Item&quot; to include items.
                    </Typography>
                  )}

                  <Stack spacing={2}>
                    {lines.map((line, idx) => {
                      const lotEntries = lotLookups[line.id]?.entries ?? [];
                      const lotEntriesLotNumber = lotLookups[line.id]?.lotNumber ?? '';
                      const existingLotBud =
                        lotEntries.find(
                          (e) =>
                            e.fulfillingPharmacyId === values.receivedAtPharmacyId &&
                            e.expirationDate
                        ) ?? lotEntries.find((e) => e.expirationDate);
                      const budLocked = Boolean(existingLotBud) && Boolean(line.bud);
                      // Same rule the submit gate uses, so the banner and the gate cannot
                      // disagree about whether this line is recordable.
                      const lotNdcMismatch = ndcSelected[line.id]
                        ? getLotNdcMismatch({ entries: lotEntries, ndc: line.ndc })
                        : [];
                      const receivingUnverified = getUnverifiedAtPharmacy({
                        entries: lotEntries,
                        fulfillingPharmacyId: values.receivedAtPharmacyId,
                      });
                      const tallyState = receivingUnverified
                        ? getTallyState({
                            unverifiedQty: receivingUnverified.unverifiedQty,
                            quantityReceived: line.totalQuantityReceived,
                            tallyQty: line.tallyQty,
                          })
                        : null;
                      const unverifiedNote = receivingUnverified
                        ? `${formatUnverifiedQty(receivingUnverified.unverifiedQty)} units of ` +
                          `${lotEntriesLotNumber} ${SYSTEM_DEFICIT.toLowerCase()} at ${pharmacyNameById(
                            receivingUnverified.fulfillingPharmacyId
                          )}`
                        : '';
                      // POM-6111 — only a tally that actually moves quantity needs a reason. At
                      // zero nothing crosses between the buckets and no ledger row is written,
                      // so there is nothing to explain.
                      const tallyReasonMissing =
                        showLineErrors &&
                        (tallyState?.effective ?? 0) > 0 &&
                        String(line.tallyReason ?? '').trim() === '';
                      const lotNdcMismatchDetail = `${lotEntriesLotNumber} is recorded against ${lotNdcMismatch
                        .map(describeLotDrug)
                        .join(', ')}.`;
                      // POM-6086 — the row this lot number is already held as, dated or not.
                      // Wider than existingLotBud above, which needs a DATED row because it is
                      // choosing the BUD to adopt; a repeat is a repeat either way.
                      const repeatedLot = ndcSelected[line.id]
                        ? getExistingLotForNumber({ entries: lotEntries, ndc: line.ndc })
                        : null;
                      const repeatedLotFacility = repeatedLot
                        ? pharmacyNameById(repeatedLot.fulfillingPharmacyId)
                        : '';
                      const repeatedLotDetail = repeatedLot
                        ? `${lotEntriesLotNumber} is already recorded for ` +
                          `${describeLotDrug(repeatedLot)}` +
                          `${repeatedLotFacility ? ` at ${repeatedLotFacility}` : ''}, on hand ` +
                          `${formatUnverifiedQty(repeatedLot.remainingQty)}. The same lot can ` +
                          'arrive twice and can sit at two facilities, so this is allowed — say ' +
                          'which it is and the receipt goes through.'
                        : '';
                      const existingLotReasonNeeded = needsExistingLotReason({
                        existingLot: repeatedLot,
                        tallyState,
                      });
                      const existingLotReasonMissing =
                        showLineErrors &&
                        existingLotReasonNeeded &&
                        String(line.existingLotReason ?? '').trim() === '';
                      const activeSplits = line.splits.filter(isDirtySplit);
                      const splitTotal = activeSplits.reduce(
                        (sum, s) => sum + (Number(s.quantityExpected) || 0),
                        0
                      );
                      const receivedQty = Number(line.totalQuantityReceived) || 0;
                      const distributableQty = Math.max(
                        0,
                        receivedQty - (tallyState?.effective ?? 0)
                      );
                      const remaining = Math.max(0, distributableQty - splitTotal);
                      const splitOverflow =
                        activeSplits.length > 0 && splitTotal > distributableQty;
                      const usedPharmacyIds = activeSplits.map((s) => s.fulfillingPharmacyId);
                      const splitPharmacies = fulfillingPharmacies.filter(
                        (p) => p.fulfillingPharmacyId !== values.receivedAtPharmacyId
                      );
                      const offage = getOffage(line);
                      const status = getLineStatus(line, ndcSelected[line.id]);

                      return (
                        <Collapse key={line.id} in appear timeout={260}>
                          <Paper variant='outlined' sx={{ borderRadius: 2, overflow: 'hidden' }}>
                            {/* Card header — the whole row toggles the item, matching accordion behaviour */}
                            <Box
                              onClick={() => handleToggleLine(line.id)}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                px: 2,
                                py: 1,
                                bgcolor: 'grey.50',
                                gap: 1,
                                cursor: 'pointer',
                                userSelect: 'none',
                              }}
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.75,
                                  flexGrow: 1,
                                }}
                              >
                                <Typography
                                  variant='body2'
                                  color='text.secondary'
                                  sx={{ fontWeight: 600 }}
                                >
                                  Item {lines.length - idx}
                                </Typography>
                                {ndcDrugName[line.id] && (
                                  <Typography
                                    variant='body2'
                                    sx={{ fontWeight: 700, color: 'primary.main' }}
                                  >
                                    · {ndcDrugName[line.id]}
                                  </Typography>
                                )}
                              </Box>
                              {status === 'incomplete' && (
                                <Box
                                  sx={{ display: 'flex', alignItems: 'center', gap: 0.25, mr: 1 }}
                                >
                                  <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                                  <Typography variant='caption' color='text.disabled'>
                                    missing required fields
                                  </Typography>
                                </Box>
                              )}
                              {status === 'complete' && (
                                <Chip
                                  icon={<CheckCircleOutlineIcon />}
                                  label='Complete'
                                  color='success'
                                  size='small'
                                />
                              )}
                              <IconButton
                                size='small'
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleToggleLine(line.id);
                                }}
                                aria-label='Toggle item'
                                aria-expanded={isLineExpanded(line.id)}
                                sx={{
                                  transition: 'transform 0.2s',
                                  transform: isLineExpanded(line.id)
                                    ? 'rotate(180deg)'
                                    : 'rotate(0deg)',
                                }}
                              >
                                <ExpandMoreIcon fontSize='small' />
                              </IconButton>
                              <IconButton
                                size='small'
                                color='error'
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRemoveLine(line.id);
                                }}
                                aria-label='Remove item'
                              >
                                <DeleteOutlineIcon fontSize='small' />
                              </IconButton>
                            </Box>
                            <Collapse in={isLineExpanded(line.id)} timeout={220} unmountOnExit>
                              <Divider />
                              <Box sx={{ px: 2, pt: 2.5, pb: 2 }}>
                                {lotNdcMismatch.length > 0 && (
                                  <LineNotice
                                    tone='error'
                                    testId={`lot-ndc-mismatch-${line.id}`}
                                    title='Lot belongs to a different drug'
                                    detail={lotNdcMismatchDetail}
                                  />
                                )}
                                {/*
                                  POM-6086 — this dialog said nothing at all when a lot number
                                  was already in inventory: the only sign was the BUD field
                                  quietly locking. On-hand for that lot is about to rise a second
                                  time, so the operator is told, and the receipt records which of
                                  three things happened. Nothing is merged and there is no
                                  override — the reason IS the resolution.
                                */}
                                {repeatedLot && (
                                  <LineNotice
                                    tone='warning'
                                    testId={`existing-lot-notice-${line.id}`}
                                    title='This lot number is already in inventory'
                                    detail={repeatedLotDetail}
                                  />
                                )}
                                {existingLotReasonNeeded && (
                                  <Box sx={{ mb: 2.5 }}>
                                    {/*
                                      The question is a label, not an answer. As a floating
                                      InputLabel it sat unshrunk inside an empty outlined Select —
                                      the very spot the chosen reason appears in — so the field
                                      read as though it had already been answered. Stated above the
                                      control instead, with the Select carrying its own placeholder
                                      until a reason is picked.
                                    */}
                                    <FormControl
                                      size='small'
                                      sx={{ minWidth: 340 }}
                                      error={existingLotReasonMissing}
                                    >
                                      <FormLabel
                                        id={`existing-lot-reason-label-${line.id}`}
                                        sx={{ mb: 0.5, fontSize: 13, fontWeight: 600 }}
                                      >
                                        Why is this lot already in inventory? *
                                      </FormLabel>
                                      <Select
                                        labelId={`existing-lot-reason-label-${line.id}`}
                                        displayEmpty
                                        value={line.existingLotReason ?? ''}
                                        renderValue={(code) =>
                                          EXISTING_LOT_REASON_OPTIONS.find((o) => o.code === code)
                                            ?.label ?? (
                                            <Box component='span' sx={{ color: 'text.disabled' }}>
                                              Select a reason
                                            </Box>
                                          )
                                        }
                                        onChange={(e) =>
                                          handleLineChange(idx, 'existingLotReason', e.target.value)
                                        }
                                        inputProps={{
                                          'data-testid': `existing-lot-reason-${line.id}`,
                                        }}
                                      >
                                        {EXISTING_LOT_REASON_OPTIONS.map(({ code, label }) => (
                                          <MenuItem key={code} value={code}>
                                            {label}
                                          </MenuItem>
                                        ))}
                                      </Select>
                                      {existingLotReasonMissing && (
                                        <FormHelperText>Required</FormHelperText>
                                      )}
                                    </FormControl>
                                  </Box>
                                )}
                                {/* Row 1: NDC | Lot | BUD */}
                                <Grid container spacing={2} sx={{ mb: 2 }}>
                                  <Grid item xs={5}>
                                    <NdcAutocomplete
                                      value={line.ndc}
                                      onChange={({ ndc, drugName, isSelected }) => {
                                        handleLineChange(idx, 'ndc', ndc);
                                        setNdcDrugName((prev) => ({
                                          ...prev,
                                          [line.id]: drugName,
                                        }));
                                        setNdcSelected((prev) => ({
                                          ...prev,
                                          [line.id]: isSelected,
                                        }));
                                      }}
                                      error={
                                        showLineErrors &&
                                        (!isValidNdc(line.ndc) || !ndcSelected[line.id])
                                      }
                                      helperText={getNdcHelperText(
                                        showLineErrors,
                                        line.ndc,
                                        ndcSelected[line.id]
                                      )}
                                    />
                                  </Grid>
                                  <Grid item xs={4}>
                                    <Stack spacing={1}>
                                      <MuiTextField
                                        label='Lot Number *'
                                        size='small'
                                        fullWidth
                                        disabled={!ndcSelected[line.id]}
                                        value={line.lotNumber}
                                        onChange={(e) => {
                                          handleLineChange(idx, 'lotNumber', e.target.value);
                                          lookupLotWhenConfirmed({
                                            lineId: line.id,
                                            lotNumber: e.target.value,
                                            confirmLotNumber: line.confirmLotNumber,
                                            receivedAtPharmacyId: values.receivedAtPharmacyId,
                                          });
                                        }}
                                        error={lotFieldError(showLineErrors, line.lotNumber)}
                                        helperText={getLotHelperText(
                                          showLineErrors,
                                          line.lotNumber
                                        )}
                                      />
                                      <MuiTextField
                                        label='Re-enter Lot Number *'
                                        size='small'
                                        fullWidth
                                        disabled={!ndcSelected[line.id]}
                                        value={line.confirmLotNumber ?? ''}
                                        onChange={(e) => {
                                          handleLineChange(idx, 'confirmLotNumber', e.target.value);
                                          lookupLotWhenConfirmed({
                                            lineId: line.id,
                                            lotNumber: line.lotNumber,
                                            confirmLotNumber: e.target.value,
                                            receivedAtPharmacyId: values.receivedAtPharmacyId,
                                          });
                                        }}
                                        inputProps={NO_PASTE_INPUT_PROPS}
                                        error={lotConfirmFieldError(
                                          showLineErrors,
                                          line.lotNumber,
                                          line.confirmLotNumber
                                        )}
                                        helperText={getLotConfirmHelperText(
                                          showLineErrors,
                                          line.lotNumber,
                                          line.confirmLotNumber
                                        )}
                                      />
                                    </Stack>
                                  </Grid>
                                  <Grid item xs={3}>
                                    <BudDateField
                                      value={line.bud}
                                      onChange={(newValue) =>
                                        handleLineChange(idx, 'bud', newValue)
                                      }
                                      disabled={budLocked}
                                      error={!budLocked && budFieldError(showLineErrors, line.bud)}
                                      helperText={(() => {
                                        // Say so when the typed date was replaced. Silently
                                        // overwriting a date the user entered — into a field that
                                        // then locks — is how they end up submitting a receipt
                                        // they did not describe.
                                        if (line.budReplacedFrom) {
                                          return `Replaced ${format(
                                            line.budReplacedFrom,
                                            'MM/dd/yyyy'
                                          )} — this is the existing lot's date`;
                                        }
                                        return budLocked
                                          ? 'From the existing lot'
                                          : getBudHelperText(showLineErrors, line.bud);
                                      })()}
                                    />
                                  </Grid>
                                </Grid>

                                {/* Row 2: Qty fields + offage chip */}
                                <Grid container spacing={2} alignItems='flex-end'>
                                  <Grid item xs={3}>
                                    <MuiTextField
                                      label='Qty Ordered *'
                                      size='small'
                                      fullWidth
                                      type='number'
                                      inputProps={{ min: 1, style: { textAlign: 'left' } }}
                                      value={line.quantityOrdered}
                                      onChange={(e) =>
                                        handleLineChange(idx, 'quantityOrdered', e.target.value)
                                      }
                                      error={orderedFieldError(showLineErrors, line)}
                                      helperText={getOrderedHelperText(showLineErrors, line)}
                                    />
                                  </Grid>
                                  <Grid item xs={3}>
                                    <MuiTextField
                                      label='Qty Shipped *'
                                      size='small'
                                      fullWidth
                                      type='number'
                                      inputProps={{ min: 1, style: { textAlign: 'left' } }}
                                      value={line.quantityShipped}
                                      onChange={(e) =>
                                        handleLineChange(idx, 'quantityShipped', e.target.value)
                                      }
                                      error={shippedFieldError(showLineErrors, line)}
                                      helperText={getShippedHelperText(showLineErrors, line)}
                                    />
                                  </Grid>
                                  <Grid item xs={3}>
                                    <MuiTextField
                                      label='Qty Received *'
                                      size='small'
                                      fullWidth
                                      type='number'
                                      inputProps={{ min: 1, style: { textAlign: 'left' } }}
                                      value={line.totalQuantityReceived}
                                      onChange={(e) =>
                                        handleLineChange(
                                          idx,
                                          'totalQuantityReceived',
                                          e.target.value
                                        )
                                      }
                                      error={receivedFieldError(showLineErrors, line)}
                                      helperText={getReceivedHelperText(showLineErrors, line)}
                                    />
                                  </Grid>
                                  <Grid
                                    item
                                    xs={3}
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      alignSelf: 'flex-start',
                                      flexWrap: 'wrap',
                                      gap: 1,
                                      py: '12px',
                                    }}
                                  >
                                    {offage > 0 && (
                                      <Chip
                                        icon={<WarningAmberIcon />}
                                        label={`${offage} offage`}
                                        size='small'
                                        variant='outlined'
                                        sx={{
                                          borderColor: '#ed6c02',
                                          color: '#ed6c02',
                                          '& .MuiChip-icon': { color: '#ed6c02' },
                                        }}
                                      />
                                    )}
                                  </Grid>
                                </Grid>

                                {receivingUnverified && tallyState && (
                                  <>
                                    <Divider sx={{ mt: -1, mb: 1 }} />
                                    <Stack
                                      direction='row'
                                      alignItems='center'
                                      spacing={1}
                                      sx={{ mb: 1.25 }}
                                      data-testid={`lot-unverified-note-${line.id}`}
                                    >
                                      <Typography
                                        variant='subtitle2'
                                        sx={{ fontWeight: 700, color: 'primary.main' }}
                                      >
                                        {unverifiedNote}
                                      </Typography>
                                      <Box sx={{ flexGrow: 1 }} />
                                      {isLineTallyComplete(line, values.receivedAtPharmacyId) && (
                                        <Chip
                                          icon={<CheckCircleOutlineIcon />}
                                          label={`Tallied ${formatUnverifiedQty(tallyState.effective)}`}
                                          size='small'
                                          variant='outlined'
                                          color='success'
                                        />
                                      )}
                                    </Stack>
                                    <Grid
                                      container
                                      columnSpacing={2}
                                      rowSpacing={1.5}
                                      alignItems='flex-start'
                                      sx={{ mt: 0 }}
                                      data-testid={`lot-tally-row-${line.id}`}
                                    >
                                      <Grid item xs={4}>
                                        <MuiTextField
                                          label={`Already dispensed (max ${tallyState.max}) *`}
                                          size='small'
                                          fullWidth
                                          type='number'
                                          inputProps={{
                                            min: 0,
                                            max: tallyState.max,
                                            step: 1,
                                            'data-testid': `lot-tally-qty-${line.id}`,
                                          }}
                                          value={line.tallyQty ?? tallyState.suggested}
                                          onChange={(e) =>
                                            handleLineChange(idx, 'tallyQty', e.target.value)
                                          }
                                          error={Boolean(tallyState.error)}
                                          helperText={tallyState.error || ''}
                                        />
                                      </Grid>
                                      <Grid item xs={8}>
                                        <FormControl
                                          size='small'
                                          fullWidth
                                          error={tallyReasonMissing}
                                        >
                                          <InputLabel>Reason *</InputLabel>
                                          <Select
                                            label='Reason *'
                                            value={line.tallyReason ?? ''}
                                            onChange={(e) =>
                                              handleLineChange(idx, 'tallyReason', e.target.value)
                                            }
                                            inputProps={{
                                              'data-testid': `lot-tally-reason-${line.id}`,
                                            }}
                                          >
                                            {TALLY_REASON_OPTIONS.map(({ code, label }) => (
                                              <MenuItem key={code} value={code}>
                                                {label}
                                              </MenuItem>
                                            ))}
                                          </Select>
                                          {tallyReasonMissing && (
                                            <FormHelperText>Required</FormHelperText>
                                          )}
                                        </FormControl>
                                      </Grid>
                                      <Grid item xs={12}>
                                        {/* Notes are always optional: the reason CODE above is
                                            what the audit report groups on, and this carries
                                            whatever the code cannot say. */}
                                        <MuiTextField
                                          label='Notes'
                                          size='small'
                                          fullWidth
                                          multiline
                                          minRows={1}
                                          maxRows={3}
                                          placeholder='Optional'
                                          value={line.tallyNotes ?? ''}
                                          onChange={(e) =>
                                            handleLineChange(idx, 'tallyNotes', e.target.value)
                                          }
                                          inputProps={{
                                            maxLength: 250,
                                            'data-testid': `lot-tally-notes-${line.id}`,
                                          }}
                                        />
                                      </Grid>
                                    </Grid>
                                    <Typography
                                      variant='caption'
                                      color='text.secondary'
                                      sx={{ display: 'block', mt: 0.75, mb: 0.5, pl: 0.25 }}
                                    >
                                      {tallyState.received === null
                                        ? 'Enter Qty Received to see how much goes to the shelf.'
                                        : [
                                            ['Receiving', tallyState.received],
                                            ['already dispensed', tallyState.effective],
                                            ['to shelf', tallyState.toShelf],
                                            [
                                              `left as ${SYSTEM_DEFICIT.toLowerCase()}`,
                                              tallyState.leftUnverified,
                                            ],
                                          ]
                                            .map(
                                              ([label, value]) =>
                                                `${label} ${formatPreviewQty(value)}`
                                            )
                                            .join(' · ')}
                                    </Typography>
                                  </>
                                )}

                                <OffageReasonsSection
                                  offage={offage}
                                  offageReasons={line.offageReasons ?? []}
                                  showErrors={showLineErrors}
                                  onChange={(next) => handleLineChange(idx, 'offageReasons', next)}
                                />

                                {/* Distribution Accordion */}
                                <Accordion
                                  disableGutters
                                  elevation={0}
                                  disabled={!values.receivedAtPharmacyId}
                                  expanded={
                                    Boolean(values.receivedAtPharmacyId) &&
                                    Boolean(distributionOpen[line.id])
                                  }
                                  onChange={(_, isExpanded) => {
                                    if (!values.receivedAtPharmacyId) return;
                                    setDistributionOpen((prev) => ({
                                      ...prev,
                                      [line.id]: isExpanded,
                                    }));
                                    if (isExpanded && line.splits.length === 0) {
                                      handleAddSplit(idx);
                                    }
                                  }}
                                  sx={{
                                    mt: 0.5,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: '4px !important',
                                    '&:before': { display: 'none' },
                                    '&.Mui-expanded': { mt: 0.5 },
                                  }}
                                >
                                  <AccordionSummary
                                    expandIcon={<ExpandMoreIcon />}
                                    sx={{
                                      bgcolor: 'grey.50',
                                      borderRadius: '3px',
                                      minHeight: '40px !important',
                                      '& .MuiAccordionSummary-content': {
                                        my: '6px',
                                        alignItems: 'center',
                                        gap: 1,
                                      },
                                    }}
                                  >
                                    <Typography variant='body2' sx={{ fontWeight: 600 }}>
                                      Distribution
                                    </Typography>
                                    {!values.receivedAtPharmacyId || activeSplits.length === 0 ? (
                                      <Typography variant='caption' color='text.secondary'>
                                        {values.receivedAtPharmacyId
                                          ? '· Not set — keep all units at receiving facility'
                                          : '· Select the receiving pharmacy first'}
                                      </Typography>
                                    ) : (
                                      <>
                                        <Typography variant='caption' color='text.secondary'>
                                          · {activeSplits.length}{' '}
                                          {activeSplits.length === 1 ? 'pharmacy' : 'pharmacies'},{' '}
                                          {splitTotal} units
                                        </Typography>
                                        <Chip
                                          label={`${remaining} remaining`}
                                          color={getSplitChipColor(splitOverflow, remaining)}
                                          size='small'
                                        />
                                      </>
                                    )}
                                  </AccordionSummary>
                                  <AccordionDetails sx={{ pt: 1.5, pb: 1.5 }}>
                                    {line.splits.length > 0 && (
                                      <Grid container spacing={1} sx={{ mb: 1, px: 1 }}>
                                        <Grid item xs={7}>
                                          <Typography
                                            variant='caption'
                                            color='text.secondary'
                                            sx={{ fontWeight: 600 }}
                                          >
                                            Allocate to facility
                                          </Typography>
                                        </Grid>
                                        <Grid item xs={4}>
                                          <Typography
                                            variant='caption'
                                            color='text.secondary'
                                            sx={{ fontWeight: 600 }}
                                          >
                                            Qty
                                          </Typography>
                                        </Grid>
                                      </Grid>
                                    )}
                                    {line.splits.map((split, splitIdx) => {
                                      const splitDirty = isDirtySplit(split);
                                      const splitQtyFilled =
                                        String(split.quantityExpected).trim() !== '';
                                      const splitQtyInvalid =
                                        splitQtyFilled && Number(split.quantityExpected) < 1;
                                      let splitQtyHelperText = '';
                                      if (showLineErrors && splitDirty && !splitQtyFilled) {
                                        splitQtyHelperText = 'Required';
                                      } else if (splitQtyInvalid) {
                                        splitQtyHelperText = 'Min 1';
                                      }
                                      const available = splitPharmacies.filter(
                                        (p) =>
                                          !usedPharmacyIds.includes(p.fulfillingPharmacyId) ||
                                          p.fulfillingPharmacyId === split.fulfillingPharmacyId
                                      );
                                      return (
                                        <Grid
                                          key={splitIdx}
                                          container
                                          spacing={1}
                                          alignItems='flex-start'
                                          sx={{ mb: 2 }}
                                        >
                                          <Grid item xs={7}>
                                            <FormControl
                                              size='small'
                                              fullWidth
                                              error={
                                                showLineErrors &&
                                                splitDirty &&
                                                !split.fulfillingPharmacyId
                                              }
                                            >
                                              <InputLabel>Facility *</InputLabel>
                                              <Select
                                                label='Facility *'
                                                value={split.fulfillingPharmacyId}
                                                onChange={(e) =>
                                                  handleSplitChange(
                                                    idx,
                                                    splitIdx,
                                                    'fulfillingPharmacyId',
                                                    e.target.value
                                                  )
                                                }
                                              >
                                                {available.map(
                                                  ({
                                                    fulfillingPharmacyId,
                                                    fulfillingPharmacyName,
                                                  }) => (
                                                    <MenuItem
                                                      key={fulfillingPharmacyId}
                                                      value={fulfillingPharmacyId}
                                                    >
                                                      {facilityLabel(
                                                        fulfillingPharmacyId,
                                                        fulfillingPharmacyName
                                                      )}
                                                    </MenuItem>
                                                  )
                                                )}
                                              </Select>
                                              {(() => {
                                                const splitUnverified = getUnverifiedAtPharmacy({
                                                  entries: lotEntries,
                                                  fulfillingPharmacyId: split.fulfillingPharmacyId,
                                                });
                                                return splitUnverified ? (
                                                  <FormHelperText
                                                    sx={{
                                                      display: 'flex',
                                                      alignItems: 'center',
                                                      gap: 0.5,
                                                      color: 'info.main',
                                                    }}
                                                  >
                                                    <InfoOutlinedIcon sx={{ fontSize: 14 }} />
                                                    {/* POM-6173 — stateable here, settleable
                                                        only at verification: this split has not
                                                        touched a lot at that facility yet. */}
                                                    {formatUnverifiedQty(
                                                      splitUnverified.unverifiedQty
                                                    )}{' '}
                                                    units of this lot {SYSTEM_DEFICIT.toLowerCase()}{' '}
                                                    at this pharmacy — settle it when you confirm
                                                    the qty received
                                                  </FormHelperText>
                                                ) : null;
                                              })()}
                                            </FormControl>
                                          </Grid>
                                          <Grid item xs={4}>
                                            <MuiTextField
                                              label='Qty Expected *'
                                              size='small'
                                              fullWidth
                                              type='number'
                                              inputProps={{
                                                min: 1,
                                                style: { textAlign: 'right' },
                                              }}
                                              value={split.quantityExpected}
                                              onChange={(e) =>
                                                handleSplitChange(
                                                  idx,
                                                  splitIdx,
                                                  'quantityExpected',
                                                  e.target.value
                                                )
                                              }
                                              error={
                                                (showLineErrors && splitDirty && !splitQtyFilled) ||
                                                splitQtyInvalid
                                              }
                                              helperText={splitQtyHelperText}
                                            />
                                          </Grid>
                                          <Grid
                                            item
                                            xs={1}
                                            sx={{
                                              display: 'flex',
                                              alignItems: 'center',
                                              pt: '6px !important',
                                            }}
                                          >
                                            <IconButton
                                              size='small'
                                              color='error'
                                              onClick={() => handleRemoveSplit(idx, splitIdx)}
                                              aria-label='Remove distribution'
                                            >
                                              <DeleteOutlineIcon fontSize='small' />
                                            </IconButton>
                                          </Grid>
                                        </Grid>
                                      );
                                    })}
                                    {splitOverflow && (
                                      <Typography
                                        variant='caption'
                                        color='error'
                                        sx={{ display: 'block', mb: 1 }}
                                      >
                                        Total distribution qty ({splitTotal}) exceeds received qty (
                                        {receivedQty}).
                                      </Typography>
                                    )}
                                    <Button
                                      size='small'
                                      variant='outlined'
                                      startIcon={<AddIcon />}
                                      onClick={() => handleAddSplit(idx)}
                                      sx={{ mt: 0.5 }}
                                    >
                                      Add distribution
                                    </Button>
                                  </AccordionDetails>
                                </Accordion>
                              </Box>
                            </Collapse>
                          </Paper>
                        </Collapse>
                      );
                    })}
                  </Stack>
                </Box>
              </DialogContent>

              <Divider />
              <DialogActions sx={{ px: 3, py: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                  <Button
                    size='medium'
                    variant='text'
                    color='info'
                    startIcon={<InfoOutlinedIcon />}
                    sx={{ textTransform: 'none' }}
                    onMouseEnter={(e) => setHowItWorksAnchor(e.currentTarget)}
                    onMouseLeave={() => setHowItWorksAnchor(null)}
                  >
                    How it works
                  </Button>
                  <Popover
                    open={Boolean(howItWorksAnchor)}
                    anchorEl={howItWorksAnchor}
                    onClose={() => setHowItWorksAnchor(null)}
                    anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    disableRestoreFocus
                    sx={{ pointerEvents: 'none' }}
                    slotProps={{
                      paper: {
                        sx: {
                          maxWidth: 520,
                          borderLeft: '4px solid',
                          borderColor: 'info.main',
                          p: 2,
                          pointerEvents: 'none',
                        },
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                      <InfoOutlinedIcon fontSize='small' sx={{ color: 'info.main' }} />
                      <Typography variant='subtitle2' sx={{ fontWeight: 700 }}>
                        How it works
                      </Typography>
                    </Box>
                    <Typography
                      variant='body2'
                      color='text.secondary'
                      component='ol'
                      sx={{ m: 0, pl: 2.5 }}
                    >
                      <li style={{ marginBottom: 6 }}>
                        Fill in the vendor details, manifest reference, received date, and the
                        receiving pharmacy on the left.
                      </li>
                      <li style={{ marginBottom: 6 }}>
                        Add one line item per product — identified by NDC, lot number, BUD, and
                        ordered / shipped / received quantities.
                      </li>
                      <li>
                        Optionally allocate received stock to other facilities using Distribution
                        splits. Total distributed cannot exceed qty received.
                      </li>
                    </Typography>
                  </Popover>
                </Box>
                <Button onClick={handleClose} color='secondary'>
                  Close
                </Button>
                <LoadingButton
                  loading={submitting}
                  type='submit'
                  variant='contained'
                  disabled={
                    invalid ||
                    !vendorIsFromList ||
                    !lines.length ||
                    !lines.every(
                      (line) =>
                        isLineValid(line) &&
                        !!ndcSelected[line.id] &&
                        isOffageReasonsValid(line) &&
                        isLineTallyComplete(line, values.receivedAtPharmacyId) &&
                        isLineExistingLotReasonComplete(line, values.receivedAtPharmacyId) &&
                        !hasForeignNdcLot(line)
                    )
                  }
                >
                  Submit Receipt
                </LoadingButton>
              </DialogActions>
            </form>
          )}
        />
      </Dialog>
      <Dialog open={open && resumePromptOpen} maxWidth='xs' fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Resume unsubmitted stock receipt?</DialogTitle>
        <DialogContent>
          <Typography variant='body2' color='text.secondary'>
            You have an unsubmitted stock receipt saved from earlier. Resume where you left off, or
            start a new one? Starting new will discard the saved entries.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleStartNew} color='secondary'>
            Start New
          </Button>
          <Button onClick={handleResumeDraft} variant='contained'>
            Resume
          </Button>
        </DialogActions>
      </Dialog>
      <AddCatalogDialog open={catalogDialogOpen} onClose={() => setCatalogDialogOpen(false)} />
      <AddInventoryItemDialog open={ndcDialogOpen} onClose={() => setNdcDialogOpen(false)} />
    </>
  );
};

AddStockDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default AddStockDialog;
